import React from 'react';
import { PlayCircle } from 'lucide-react';
import PresentationRenderer from './PresentationRenderer';

const CourseDetailTab = ({
  selectedCourse,
  quizDataCache,
  quizAnswers,
  quizSubmitted,
  onBack,
  onQuizAnswer,
  onQuizSubmit,
  onCompleteCourse,
  onQuizRetry,
}) => {
  if (!selectedCourse) return null;

  return (
    <div>
      <button
        onClick={onBack}
        className="btn-secondary"
        style={{ marginBottom: '1.5rem' }}
        data-testid="back-to-courses-btn"
      >
        &#8592; Retour aux cours
      </button>

      <div className="card" style={{ padding: '2rem' }}>
        {selectedCourse.type === 'presentation' && selectedCourse.isInteractive && (
          <div style={{ marginBottom: '2rem' }}>
            <PresentationRenderer presentationType={selectedCourse.presentationType} />
          </div>
        )}

        {selectedCourse.type === 'video' && (
          <div style={{ marginBottom: '2rem' }}>
            {selectedCourse.videoUrl ? (
              <div style={{ marginBottom: '1rem' }}>
                <iframe
                  width="100%"
                  height="500"
                  src={selectedCourse.videoUrl}
                  title={selectedCourse.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ borderRadius: '0.75rem' }}
                />
              </div>
            ) : (
              <div style={{
                background: 'var(--accent-purple-200)',
                height: '400px',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '1rem'
              }}>
                <PlayCircle size={64} />
              </div>
            )}
            <p className="body-small" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
              Lecteur vid&eacute;o - Dur&eacute;e : {selectedCourse.duration}
            </p>
          </div>
        )}

        {selectedCourse.type === 'quiz' && (
          <div>
            {quizDataCache[selectedCourse.id] && (
              <div>
                <h3 className="heading-2" style={{ marginBottom: '1.5rem' }}>
                  {quizDataCache[selectedCourse.id].title}
                </h3>
                {quizDataCache[selectedCourse.id].questions.map((question, qIndex) => (
                  <div key={question.id} className="card accent-grey" style={{ marginBottom: '1.5rem' }}>
                    <p className="body-medium" style={{ marginBottom: '1rem', fontWeight: 500 }}>
                      {qIndex + 1}. {question.question}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {question.options.map((option, oIndex) => (
                        <label
                          key={oIndex}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.75rem',
                            padding: '0.75rem',
                            background: quizAnswers[question.id] === oIndex ? 'var(--accent-blue-200)' : 'white',
                            borderRadius: '0.5rem',
                            cursor: quizSubmitted ? 'default' : 'pointer',
                            border: quizSubmitted && oIndex === question.correct ? '2px solid green' :
                                    quizSubmitted && quizAnswers[question.id] === oIndex && oIndex !== question.correct ? '2px solid red' : 'none'
                          }}
                        >
                          <input
                            type="radio"
                            name={question.id}
                            checked={quizAnswers[question.id] === oIndex}
                            onChange={() => !quizSubmitted && onQuizAnswer(question.id, oIndex)}
                            disabled={quizSubmitted}
                          />
                          <span className="body-small">{option}</span>
                        </label>
                      ))}
                    </div>
                    {quizSubmitted && question.explanation && (
                      <div style={{ marginTop: '0.75rem', padding: '0.75rem', background: '#e8f5e9', borderRadius: '0.5rem', borderLeft: '3px solid #4caf50' }}>
                        <p className="body-small" style={{ color: '#333', fontStyle: 'italic' }}>{question.explanation}</p>
                      </div>
                    )}
                  </div>
                ))}
                {!quizSubmitted && (
                  <button
                    onClick={onQuizSubmit}
                    className="btn-primary"
                    style={{ width: '100%', justifyContent: 'center' }}
                    data-testid="quiz-submit-btn"
                  >
                    Valider le quiz
                  </button>
                )}
                {quizSubmitted && (
                  <div style={{ marginTop: '1rem' }}>
                    {(() => {
                      const quizData = quizDataCache[selectedCourse.id];
                      if (!quizData) return null;
                      let correct = 0;
                      quizData.questions.forEach(q => { if (quizAnswers[q.id] === q.correct) correct++; });
                      const pct = Math.round((correct / quizData.questions.length) * 100);
                      const passed = pct >= 70;
                      return (
                        <div style={{
                          padding: '1.5rem',
                          background: passed
                            ? 'linear-gradient(135deg, #d1fae5, #a7f3d0)'
                            : 'linear-gradient(135deg, #fee2e2, #fecaca)',
                          borderRadius: '0.75rem', textAlign: 'center',
                          border: passed ? '2px solid #059669' : '2px solid #DC2626'
                        }}>
                          <h3 className="heading-2" style={{ marginBottom: '0.5rem', color: passed ? '#059669' : '#DC2626' }}>
                            {passed ? 'Quiz validé !' : 'Quiz non validé'}
                          </h3>
                          <p className="body-medium" data-testid="quiz-result" style={{ marginBottom: '0.75rem' }}>
                            {correct} / {quizData.questions.length} bonnes réponses ({pct}%)
                          </p>
                          <p style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1rem' }}>
                            {passed
                              ? 'Félicitations ! Le bloc suivant est maintenant débloqué.'
                              : 'Il faut au moins 70% de bonnes réponses pour valider et débloquer le bloc suivant.'}
                          </p>
                          {!passed && onQuizRetry && (
                            <button
                              onClick={onQuizRetry}
                              data-testid="quiz-retry-btn"
                              style={{
                                padding: '0.75rem 2rem', borderRadius: '8px',
                                background: '#DC2626', color: 'white', border: 'none',
                                fontWeight: 600, cursor: 'pointer', fontSize: '0.95rem'
                              }}
                            >
                              Recommencer le quiz
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!quizSubmitted && (
          <button
            onClick={onCompleteCourse}
            className="btn-primary"
            style={{ width: '100%', justifyContent: 'center', marginTop: '2rem' }}
            data-testid="complete-course-btn"
          >
            Marquer comme termin&eacute;
          </button>
        )}
      </div>
    </div>
  );
};

export default CourseDetailTab;
